// Web Mercator tile math, isolated as pure functions so they're trivially
// testable and reusable. All inputs are plain numbers; no Konva or DOM.
//
// Tile index convention (OSM / Google / MapTiler / Leaflet):
//   - z = 0 covers the whole world in one 256×256 tile.
//   - At zoom z there are 2^z tiles along each axis.
//   - x grows east (longitude), y grows south (latitude — flipped vs. lat).
//   - The tile at (x, y, z) has NW corner at tileToLngLat(x, y, z) and SE
//     corner at tileToLngLat(x + 1, y + 1, z).

const EARTH_CIRCUMFERENCE_M = 40_075_016.686 // WGS84 equator.
const TILE_PX = 256

// Fractional tile coordinates — round/floor at the call site to get an index.
export function lngLatToTile(lng, lat, z) {
  const n = 2 ** z
  const x = ((lng + 180) / 360) * n
  const latRad = (lat * Math.PI) / 180
  const y = ((1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2) * n
  return { x, y }
}

// NW corner of tile (x, y, z) in [lng, lat].
export function tileToLngLat(x, y, z) {
  const n = 2 ** z
  const lng = (x / n) * 360 - 180
  const latRad = Math.atan(Math.sinh(Math.PI * (1 - (2 * y) / n)))
  const lat = (latRad * 180) / Math.PI
  return [lng, lat]
}

// For a lng/lat bounding box, return the integer tile indices that cover it
// at zoom z. Inclusive on both ends so partial tiles at edges are included.
export function tilesForLngLatBbox({ west, south, east, north }, z) {
  const nw = lngLatToTile(west, north, z)
  const se = lngLatToTile(east, south, z)
  return {
    xMin: Math.floor(nw.x),
    yMin: Math.floor(nw.y),
    xMax: Math.floor(se.x),
    yMax: Math.floor(se.y),
  }
}

// Pick a tile zoom level that roughly matches a given editor pxPerMetre at
// the given latitude. At zoom z, Mercator pxPerMetre ≈ 2^z * 256 /
// (40075016.686 * cos(lat)). We solve for z and round to the nearest integer
// that makes tiles at least as sharp as the editor, clamped to a range MapTiler
// serves (0-22; satellite-v2 goes up to ~20).
export function pickTileZoom(pxPerMetre, latDeg, { min = 15, max = 20 } = {}) {
  const cosLat = Math.cos((latDeg * Math.PI) / 180)
  const raw = Math.log2((pxPerMetre * EARTH_CIRCUMFERENCE_M * cosLat) / TILE_PX)
  // Round UP so tiles are always at least as detailed as the editor; this
  // keeps imagery crisp at base scale and mildly pixelated only when the
  // user pinches in past the picked zoom level.
  return Math.max(min, Math.min(max, Math.ceil(raw)))
}

// Lng/lat bounding box covering the whole visible editor canvas. The
// satellite backdrop's job is "let the user see neighbours, roads, and
// noise sources around the plot" — so we cover the entire viewport, not
// just the plot polygon.
//
// Two transforms stack on top of each other: `scale` (base fit-to-canvas,
// metres→pixels via offsetPx + pxPerMetre) and the Konva stage transform
// (pinch-zoom via stage.scale, two-finger pan via stage.position). The
// screen pixel (sx, sy) maps back to metres as:
//   local_x = (sx - stagePos.x) / stageScale
//   xM     = (local_x - offsetPx.x) / pxPerMetre
// So the visible metres box depends on both. Without this, a user who
// pans to see a neighbour's house hits a hard tile edge outside the
// original plot-centred bbox.
export function bboxLngLatFromViewport({ scale, stageWidth, stageHeight, originLngLat, stageScale = 1, stagePos = { x: 0, y: 0 } }) {
  if (!scale?.pxPerMetre || !originLngLat || !Number.isFinite(stageWidth) || !Number.isFinite(stageHeight)) return null
  const { pxPerMetre, offsetPx } = scale
  if (pxPerMetre <= 0 || stageWidth <= 0 || stageHeight <= 0 || !(stageScale > 0)) return null

  const unprojectX = (sx) => ((sx - stagePos.x) / stageScale - offsetPx.x) / pxPerMetre
  const unprojectY = (sy) => ((sy - stagePos.y) / stageScale - offsetPx.y) / pxPerMetre

  const minXM = unprojectX(0)
  const maxXM = unprojectX(stageWidth)
  const minYM = unprojectY(0)
  const maxYM = unprojectY(stageHeight)

  const [lngOrigin, latOrigin] = originLngLat
  const cosLat = Math.cos((latOrigin * Math.PI) / 180)
  const radToDeg = 180 / Math.PI
  const earthR = 6_378_137
  const mToLat = (m) => (m / earthR) * radToDeg
  const mToLng = (m) => (m / (earthR * cosLat)) * radToDeg
  // Editor y grows south; lat grows north. minYM (top of canvas) = max lat.
  return {
    west: lngOrigin + mToLng(minXM),
    east: lngOrigin + mToLng(maxXM),
    north: latOrigin - mToLat(minYM),
    south: latOrigin - mToLat(maxYM),
  }
}
